/**
 * the trading-data slice. Holds the latest snapshot of:
 *   - account info (balance / equity / margin / leverage)
 *   - open positions
 *   - pending orders
 *   - closed history (fetched on demand for the History dialog)
 *
 * Live updates arrive via the `/api/trading/stream` SSE (one process-
 * wide pump → fan-out to subscribers). The stream hook
 * (`useTradingStream`) only connects for owner sessions — guests get
 * 403 on the endpoint, so the EventSource constructor would loop
 * forever otherwise.
 *
 * `connected` mirrors whether the trading server is actually attached — when the
 * terminal isn't running the API returns `connected: false` and
 * the UI shows a "the trading server not connected" badge instead of an empty
 * list (which would otherwise read as "you have no positions").
 *
 * History remains a one-shot REST fetch via `fetchTradingHistory` —
 * it's user-triggered (opening the modal) and the date range can
 * vary, so it doesn't fit the always-on pump model.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { api } from "@/api/client";
import type {
  TradingAccountInfo,
  TradingAccountResponse,
  TradingDeal,
  TradingHistoryResponse,
  TradingOrder,
  TradingOrdersResponse,
  TradingPosition,
  TradingPositionsResponse,
} from "@/api/types";

interface TradingState {
  connected: boolean;
  account: TradingAccountInfo | null;
  positions: TradingPosition[];
  orders: TradingOrder[];
  history: { deals: TradingDeal[]; orders: TradingOrder[] };
  /** SSE connection state — drives the header chip's "live / offline"
   *  indicator independent of the trading server itself. `streamConnected` is true
   *  while the EventSource is open; `connected` is whether the
   *  server-side pump can actually talk to the trading server. */
  streamConnected: boolean;
  historyStatus: "idle" | "loading" | "succeeded" | "failed";
  /** Unix ms of the last stream event that updated any live
   *  resource (account / positions / orders). The header chip uses
   *  it for an "as of HH:MM:SS" label so a stuck pump is visible. */
  lastPolledAt: number | null;
  error: string | null;
}

const initialState: TradingState = {
  connected: false,
  account: null,
  positions: [],
  orders: [],
  history: { deals: [], orders: [] },
  streamConnected: false,
  historyStatus: "idle",
  lastPolledAt: null,
  error: null,
};

/** Shape of the `snapshot` SSE event. `warming` is sent by the
 *  pump's bootstrap window before its first poll completes — the
 *  UI should show "loading" rather than "the trading server not connected". */
interface TradingStreamSnapshot {
  warming?: boolean;
  account?: TradingAccountResponse;
  positions?: TradingPositionsResponse;
  orders?: TradingOrdersResponse;
}

export const fetchTradingHistory = createAsyncThunk<
  TradingHistoryResponse,
  { days?: number; symbol?: string } | undefined
>("trading/history", async (arg) =>
  // No `?? 30` here — omitted days = all-time history. PositionsPanel
  // still passes days: 30 explicitly for its "History 30d" tab.
  api.tradingHistory(arg?.days, arg?.symbol),
);

const tradingSlice = createSlice({
  name: "trading",
  initialState,
  reducers: {
    /** Replace the whole live slice from a `snapshot` SSE event.
     *  Only fields present in the payload are touched — that way
     *  the pump's bootstrap "warming" message (which contains
     *  nothing) doesn't accidentally clobber any state the client
     *  managed to receive between disconnect and reconnect. */
    applyTradingSnapshot: (state, action: PayloadAction<TradingStreamSnapshot>) => {
      const { account, positions, orders } = action.payload;
      if (account) {
        state.connected = account.connected;
        state.account = account.account;
      }
      if (positions) {
        state.connected = positions.connected;
        state.positions = positions.positions;
      }
      if (orders) {
        state.orders = orders.orders;
      }
      if (account || positions || orders) {
        state.lastPolledAt = Date.now();
      }
    },
    applyTradingAccount: (state, action: PayloadAction<TradingAccountResponse>) => {
      state.connected = action.payload.connected;
      state.account = action.payload.account;
      state.lastPolledAt = Date.now();
    },
    applyTradingPositions: (state, action: PayloadAction<TradingPositionsResponse>) => {
      state.connected = action.payload.connected;
      state.positions = action.payload.positions;
      state.lastPolledAt = Date.now();
    },
    applyTradingOrders: (state, action: PayloadAction<TradingOrdersResponse>) => {
      state.orders = action.payload.orders;
      state.lastPolledAt = Date.now();
    },
    /** Toggled by the stream hook on open / error so the UI can
     *  show a "live" dot vs "offline" without coupling to
     *  EventSource internals. */
    /** Wipe every trace of the previous account.
     *
     *  Disconnect must leave nothing behind: history is fetched once and
     *  cached, and the account/positions/orders survive until the next stream
     *  event — so without this the trader "disconnects" and still sees their
     *  balance and closed trades sitting there. */
    clearTrading: (state) => {
      state.connected = false;
      state.account = null;
      state.positions = [];
      state.orders = [];
      state.history = { deals: [], orders: [] };
      state.historyStatus = "idle";
    },
    setTradingStreamConnected: (state, action: PayloadAction<boolean>) => {
      state.streamConnected = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTradingHistory.pending, (state) => {
        state.historyStatus = "loading";
      })
      .addCase(fetchTradingHistory.fulfilled, (state, action) => {
        state.historyStatus = "succeeded";
        state.history = {
          deals: action.payload.deals,
          orders: action.payload.orders,
        };
      })
      .addCase(fetchTradingHistory.rejected, (state, action) => {
        state.historyStatus = "failed";
        state.error = action.error.message ?? "Failed to fetch trade history";
      });
  },
});

export const {
  applyTradingSnapshot,
  applyTradingAccount,
  applyTradingPositions,
  applyTradingOrders,
  setTradingStreamConnected,
  clearTrading,
} = tradingSlice.actions;

export default tradingSlice.reducer;
